import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Language } from '../../api';
import { STOREFRONT_LANG_KEY } from '../../consts';
import { useLanguage } from '../../utils/GeneralUtils';

const languageConfig: Record<Language, { flag: string; label: string; code: string }> = {
  [Language.EN]: { flag: '🇺🇸', label: 'English', code: 'en-US' },
  [Language.HE]: { flag: '🇮🇱', label: 'עברית', code: 'he' },
};

type LanguageSwitcherProps = {
  languages: Language[];
};

export const LanguageSwitcher = ({ languages }: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const language = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const getCurrentLanguage = () => {
    if (language === 'he') return Language.HE;
    return Language.EN;
  };

  const changeLang = (lang: string) => {
    localStorage.setItem(STOREFRONT_LANG_KEY, lang);
    void i18n.changeLanguage(lang);
    document.body.dir = i18n.dir();
    theme.direction = i18n.dir();
    setAnchorEl(null);
  };

  const currentLang = getCurrentLanguage();

  if (languages.length <= 1) return null;

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          padding: '6px 12px',
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          '&:hover': { backgroundColor: '#f5f5f5' },
        }}>
        <Typography sx={{ fontSize: 20, lineHeight: 1 }}>{languageConfig[currentLang].flag}</Typography>
        <KeyboardArrowDown sx={{ fontSize: 18, color: 'text.secondary', ml: 0.5 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            minWidth: 150,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          },
        }}>
        {languages.map((lang) => (
          <MenuItem
            key={lang}
            onClick={() => changeLang(languageConfig[lang].code)}
            selected={lang === currentLang}
            sx={{ gap: 1.5, py: 1.5 }}>
            <Typography sx={{ fontSize: 20, lineHeight: 1 }}>{languageConfig[lang].flag}</Typography>
            <Typography>{languageConfig[lang].label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
